/**
 * Outlook .msg files often store HTML inside PR_RTF_COMPRESSED rather than
 * PidTagBodyHtml / PidTagHtml. Decompress and de-encapsulate to recover it.
 */
export async function extractHtmlFromCompressedRtf(compressed: Uint8Array): Promise<string | null> {
  if (compressed.length === 0) return null;

  try {
    await ensureNodeBuffer();
    const [{ decompressRTF }, { deEncapsulateSync }] = await Promise.all([
      import('@kenjiuno/decompressrtf'),
      import('rtf-stream-parser'),
    ]);

    const decompressed = decompressRTF(Array.from(compressed));
    const rtfBytes = Uint8Array.from(decompressed as ArrayLike<number>);
    const rtf = new TextDecoder('iso-8859-1').decode(rtfBytes);

    const result = deEncapsulateSync(rtf, {
      mode: 'html',
      decode: decodeRtfText,
    });

    if (result.mode !== 'html') return null;
    const html = typeof result.text === 'string'
      ? result.text
      : new TextDecoder('utf-8', { fatal: false }).decode(toUint8Array(result.text));
    return html.trim() ? html : null;
  } catch {
    return null;
  }
}

/** rtf-stream-parser expects a Node Buffer global; Vite only aliases the module. */
async function ensureNodeBuffer(): Promise<void> {
  const g = globalThis as typeof globalThis & { Buffer?: unknown; global?: typeof globalThis };
  if (typeof g.global === 'undefined') {
    g.global = globalThis;
  }
  if (typeof g.Buffer !== 'undefined') return;
  const { Buffer } = await import('buffer');
  g.Buffer = Buffer;
}

function decodeRtfText(data: Uint8Array | { buffer: ArrayBuffer; byteOffset?: number; byteLength?: number }, encoding: string): string {
  const bytes = toUint8Array(data);
  const normalized = normalizeRtfEncoding(encoding);
  try {
    return new TextDecoder(normalized).decode(bytes);
  } catch {
    return new TextDecoder('iso-8859-1').decode(bytes);
  }
}

function toUint8Array(data: Uint8Array | ArrayBuffer | { buffer: ArrayBuffer; byteOffset?: number; byteLength?: number }): Uint8Array {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  const view = data as { buffer: ArrayBuffer; byteOffset?: number; byteLength?: number };
  return new Uint8Array(view.buffer, view.byteOffset ?? 0, view.byteLength ?? view.buffer.byteLength);
}

function normalizeRtfEncoding(encoding: string): string {
  const lower = encoding.toLowerCase();
  if (lower === 'cp0' || lower === 'default') return 'iso-8859-1';
  if (lower.startsWith('cp')) {
    const cp = Number.parseInt(lower.slice(2), 10);
    if (cp === 1252) return 'windows-1252';
    if (cp === 1250) return 'windows-1250';
    if (cp === 1251) return 'windows-1251';
    if (cp === 932) return 'shift_jis';
    if (cp === 936) return 'gbk';
    if (cp === 949) return 'euc-kr';
  }
  if (lower === 'utf-8' || lower === 'utf8') return 'utf-8';
  return lower;
}

export function decodeMsgHtmlBytes(bytes: Uint8Array, codepage?: number): string {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  }

  const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  if (looksLikeHtml(utf8)) return utf8;

  if (codepage != null) {
    const encoding = codepageToEncoding(codepage);
    if (encoding) {
      try {
        const decoded = new TextDecoder(encoding).decode(bytes);
        if (looksLikeHtml(decoded)) return decoded;
      } catch {
        // fall through
      }
    }
  }

  return utf8;
}

function looksLikeHtml(text: string): boolean {
  const sample = text.trim().slice(0, 2000).toLowerCase();
  return sample.includes('<html') || sample.includes('<body') || sample.includes('<div') || sample.includes('<p');
}

function codepageToEncoding(codepage: number): string | null {
  switch (codepage) {
    case 65001:
      return 'utf-8';
    case 1252:
      return 'windows-1252';
    case 1250:
      return 'windows-1250';
    case 1251:
      return 'windows-1251';
    case 932:
      return 'shift_jis';
    case 936:
      return 'gbk';
    case 949:
      return 'euc-kr';
    default:
      return null;
  }
}
